import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Eye, Zap } from 'lucide-react';
import { useStore } from '../stores/appStore';
import { formatPrice, generateWhatsAppLink, generateMotoWhatsAppMessage, getMotoStatus } from '../utils';
import { Moto } from '../types';

export const MotoGallery: React.FC = () => {
  const { motos, incrementVisualizacao } = useStore();
  const [selectedMoto, setSelectedMoto] = useState<Moto | null>(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const handleMotoClick = (moto: Moto) => {
    setSelectedMoto(moto);
    setCurrentImageIndex(0);
    incrementVisualizacao(moto.id);
  };

  const handleWhatsAppClick = (moto: Moto) => {
    const message = generateMotoWhatsAppMessage(moto);
    const link = generateWhatsAppLink('5513996763208', message);
    window.open(link, '_blank');
  };

  const nextImage = () => {
    if (selectedMoto) {
      setCurrentImageIndex((prev) => 
        prev === selectedMoto.imagens.length - 1 ? 0 : prev + 1
      );
    }
  };

  const prevImage = () => {
    if (selectedMoto) {
      setCurrentImageIndex((prev) => 
        prev === 0 ? selectedMoto.imagens.length - 1 : prev - 1
      );
    }
  };

  return (
    <section className="py-16 bg-gray-50">
      <div className="container-custom">
        <div className="text-center mb-12">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
            Nossas Motos Disponíveis
          </h2>
          <p className="text-lg text-gray-600">
            Escolha a moto ideal para você com as melhores condições
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {motos.map((moto) => {
            const status = getMotoStatus(moto);
            const hasPromo = moto.precoPromocional && moto.precoPromocional < moto.preco;
            
            return (
              <div
                key={moto.id}
                className="card cursor-pointer transform hover:scale-105 transition-transform duration-300"
                onClick={() => handleMotoClick(moto)}
              >
                <div className="relative">
                  <img
                    src={moto.imagens[0]}
                    alt={`${moto.nome} ${moto.modelo}`}
                    className="w-full h-64 object-cover rounded-t-xl"
                  />
                  <div className="absolute top-4 left-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${status.className}`}>
                      {status.text}
                    </span>
                  </div>
                  {hasPromo && (
                    <div className="absolute top-4 right-4">
                      <span className="bg-red-500 text-white px-3 py-1 rounded-full text-xs font-semibold flex items-center">
                        <Zap size={12} className="mr-1" />
                        PROMOÇÃO
                      </span>
                    </div>
                  )}
                  <div className="absolute bottom-4 right-4 bg-black bg-opacity-70 text-white px-2 py-1 rounded text-sm flex items-center">
                    <Eye size={14} className="mr-1" />
                    {moto.visualizacoes}
                  </div>
                </div>

                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    {moto.nome}
                  </h3>
                  <p className="text-gray-600 mb-4">{moto.descricao}</p>
                  
                  <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
                    <div>
                      <span className="text-gray-500">Cilindrada:</span>
                      <p className="font-semibold">{moto.cilindrada}cc</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Consumo:</span>
                      <p className="font-semibold">{moto.consumo}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Ano:</span>
                      <p className="font-semibold">{moto.ano}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Estoque:</span>
                      <p className="font-semibold">{moto.estoque} un</p>
                    </div>
                  </div>

                  <div className="mb-4">
                    {hasPromo ? (
                      <div className="flex items-center space-x-2">
                        <span className="text-2xl font-bold text-primary-600">
                          {formatPrice(moto.precoPromocional!)}
                        </span>
                        <span className="text-lg text-gray-500 line-through">
                          {formatPrice(moto.preco)}
                        </span>
                      </div>
                    ) : (
                      <span className="text-2xl font-bold text-primary-600">
                        {formatPrice(moto.preco)}
                      </span>
                    )}
                  </div>

                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleWhatsAppClick(moto);
                    }}
                    className="w-full btn-primary flex items-center justify-center space-x-2"
                  >
                    <span>Tenho Interesse</span>
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Modal de Detalhes */}
      {selectedMoto && (
        <div className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="relative">
              <div className="relative h-96">
                <img
                  src={selectedMoto.imagens[currentImageIndex]}
                  alt={`${selectedMoto.nome} - Imagem ${currentImageIndex + 1}`}
                  className="w-full h-full object-cover rounded-t-xl"
                />
                
                {selectedMoto.imagens.length > 1 && (
                  <>
                    <button
                      onClick={prevImage}
                      className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-75"
                    >
                      <ChevronLeft size={24} />
                    </button>
                    <button
                      onClick={nextImage}
                      className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-75"
                    >
                      <ChevronRight size={24} />
                    </button>
                  </>
                )}
                
                <button
                  onClick={() => setSelectedMoto(null)}
                  className="absolute top-4 right-4 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-75"
                >
                  ✕
                </button>
              </div>

              <div className="p-8">
                <div className="flex justify-between items-start mb-6">
                  <div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-2">
                      {selectedMoto.nome}
                    </h2>
                    <p className="text-gray-600">{selectedMoto.descricao}</p>
                  </div>
                  <div className="text-right">
                    {selectedMoto.precoPromocional && selectedMoto.precoPromocional < selectedMoto.preco ? (
                      <div>
                        <p className="text-3xl font-bold text-primary-600">
                          {formatPrice(selectedMoto.precoPromocional)}
                        </p>
                        <p className="text-lg text-gray-500 line-through">
                          {formatPrice(selectedMoto.preco)}
                        </p>
                      </div>
                    ) : (
                      <p className="text-3xl font-bold text-primary-600">
                        {formatPrice(selectedMoto.preco)}
                      </p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-3 gap-6 mb-8">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-2">Especificações</h4>
                    <ul className="space-y-2 text-sm">
                      <li><span className="text-gray-500">Ano:</span> <span className="font-semibold">{selectedMoto.ano}</span></li>
                      <li><span className="text-gray-500">Cilindrada:</span> <span className="font-semibold">{selectedMoto.cilindrada}cc</span></li>
                      <li><span className="text-gray-500">Peso:</span> <span className="font-semibold">{selectedMoto.peso}</span></li>
                    </ul>
                  </div>
                  
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-2">Performance</h4>
                    <ul className="space-y-2 text-sm">
                      <li><span className="text-gray-500">Consumo:</span> <span className="font-semibold">{selectedMoto.consumo}</span></li>
                      <li><span className="text-gray-500">Tanque:</span> <span className="font-semibold">{selectedMoto.tanque}</span></li>
                      <li><span className="text-gray-500">Categoria:</span> <span className="font-semibold">{selectedMoto.categoria}</span></li>
                    </ul>
                  </div>
                  
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-2">Disponibilidade</h4>
                    <ul className="space-y-2 text-sm">
                      <li><span className="text-gray-500">Estoque:</span> <span className="font-semibold">{selectedMoto.estoque} unidades</span></li>
                      <li><span className="text-gray-500">Visualizações:</span> <span className="font-semibold">{selectedMoto.visualizacoes}</span></li>
                      <li><span className="text-gray-500">Status:</span> <span className="font-semibold">{getMotoStatus(selectedMoto).text}</span></li>
                    </ul>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-4">
                  <button
                    onClick={() => handleWhatsAppClick(selectedMoto)}
                    className="flex-1 btn-primary text-lg py-4"
                  >
                    Tenho Interesse - WhatsApp
                  </button>
                  <button
                    onClick={() => setSelectedMoto(null)}
                    className="px-6 py-4 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Fechar
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};